export interface CurrentSubscriptionCardProps {
  /** The name of the current plan */
  planName: string;

  /** The currency code (e.g., "USD", "EUR", "GBP") */
  currency?: string | null;

  /** The price in cents (will be divided by 100 for display) */
  price?: number | null;

  /** Whether this plan has been cancelled (scheduled for end) */
  isCancelled?: boolean;

  /** The scheduled cancellation end date */
  endDate?: string | null;

  /** Whether the button is in loading state */
  buttonLoading?: boolean;

  /** Whether the button should be disabled */
  buttonDisabled?: boolean;

  /** Error message to display */
  error?: string | null;

  onCancel: () => void;
  onReactivate: () => void;
}

export function CurrentSubscriptionCard({
  planName,
  currency,
  price,
  isCancelled = false,
  endDate = null,
  buttonLoading = false,
  buttonDisabled = false,
  error,
  onCancel,
  onReactivate,
}: CurrentSubscriptionCardProps) {
  // Format currency symbol
  const getCurrencySymbol = (currency: string | null | undefined) => {
    switch (currency?.toUpperCase()) {
      case 'USD':
        return '$';
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      default:
        return '€'; // Default to EUR if no currency specified
    }
  };

  // Format price (convert from cents to dollars/euros)
  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined) return '--';
    return (price / 100).toFixed(0);
  };

  const currencySymbol = getCurrencySymbol(currency);
  const formattedPrice = formatPrice(price);

  // Calculate days left from endDate
  const getDaysLeft = (): number | null => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const daysLeft = getDaysLeft();

  return (
    <div className="p-6 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-lg">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Current Plan: {planName}
            </h3>
            {isCancelled && daysLeft !== null ? (
              <span className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-full">
                {daysLeft} {daysLeft === 1 ? 'day left' : 'days left'}
              </span>
            ) : (
              <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full">
                Active
              </span>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {currencySymbol}
            {formattedPrice}
            <span className="text-sm font-normal text-gray-500">/month</span>
          </p>
          {isCancelled && endDate && (
            <p className="text-sm text-orange-600 dark:text-orange-400 mt-2">
              Your subscription is scheduled to end on{' '}
              <span className="font-semibold">
                {new Date(endDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {isCancelled ? (
            <button
              onClick={onReactivate}
              disabled={buttonDisabled || buttonLoading}
              className="px-4 py-2 bg-[#E34290] text-white font-medium rounded-lg hover:bg-[#E34290]/90 transition-colors disabled:opacity-60"
            >
              {buttonLoading ? 'Reactivating...' : 'Reactivate Subscription'}
            </button>
          ) : (
            <button
              onClick={onCancel}
              disabled={buttonDisabled || buttonLoading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
            >
              {buttonLoading ? 'Cancelling...' : 'Cancel Subscription'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-red-600 dark:text-red-400 mt-3 text-sm">{error}</div>}
    </div>
  );
}
